import React, { useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  styled,
  TextField,
} from '@mui/material';
import { FolderView } from 'api';
import { EditFolderDialogProps, FolderMap } from './types';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  flex: '0 0 auto',
  margin: 0,
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  paddingTop: `${theme.spacing(3)}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
  '&.MuiDialogContent-root': {
    paddingTop: theme.spacing(1),
  },
}));

const StyledTextField = styled(TextField)({
  marginBottom: '3px',
});

const StyledFormControl = styled(FormControl)(() => ({
  // Style code here
}));

const StyledInputLabel = styled(InputLabel)(() => ({
  // Style code here
}));

const StyledSelect = styled(Select)(() => ({
  // Style code here
}));

const StyledMenuItem = styled(MenuItem)(() => ({
  // Style code here
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  justifyContent: 'flex-end',
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  color: theme.palette.grey[900],
}));

const EditFolderDialog = (props: EditFolderDialogProps) => {
  const { open, onClose, folder, onSave, folders } = props;

  const folderMap = useMemo(
    () =>
      folders
        ?.filter(folderItem => folderItem.parent_folder === null)
        ?.reduce((acc, toMapFolder) => {
          if (folder.id === toMapFolder.id || toMapFolder.parent_folder?.id === folder.id) {
            return acc;
          }
          acc[toMapFolder.id as number] = toMapFolder;
          return acc;
        }, {} as FolderMap) || {},
    [folders, folder.id]
  );

  const [parentFolder, setParentFolder] = React.useState<FolderView | undefined>(
    folderMap[folder.parent_folder?.id as number] || undefined
  );
  const [folderName, setFolderName] = React.useState(folder.name);
  const handleFolderNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(event.target.value);
  };
  const handleParentFolderChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    if (event.target.value === null) {
      setParentFolder(undefined);
      return;
    }
    setParentFolder(folderMap[event.target.value as number]);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <StyledDialogTitle>Edit Folder</StyledDialogTitle>
      <StyledDialogContent>
        <StyledTextField
          label="Folder Name"
          value={folderName}
          onChange={handleFolderNameChange}
          variant="standard"
          fullWidth
        />
        <StyledFormControl fullWidth variant="standard">
          <StyledInputLabel id="parent-folder-label">Parent Folder</StyledInputLabel>
          <StyledSelect
            labelId="parent-folder-label"
            value={parentFolder?.id ?? ''}
            onChange={handleParentFolderChange}
            MenuProps={{
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}>
            <StyledMenuItem value="">
              <em>None</em>
            </StyledMenuItem>
            {Object.values(folderMap).map(folder => (
              <StyledMenuItem key={folder.id} value={folder.id}>
                {folder.name}
              </StyledMenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
      </StyledDialogContent>

      <StyledDialogActions>
        <StyledButton onClick={onClose}>Cancel</StyledButton>
        <StyledButton onClick={() => onSave(folderName, parentFolder)} variant="text">
          Save
        </StyledButton>
      </StyledDialogActions>
    </Dialog>
  );
};

export default EditFolderDialog;
